import { CreditCard, HelpCircle, Languages, LogOut, Moon, Search, Settings, SlidersHorizontal, Sun, User as UserIcon, UserPlus, X } from "lucide-react";
import { NotificationCenter } from "@/components/staff/NotificationCenter";
import { GymVizLogo } from "@/components/brand/GymVizLogo";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePublicTheme } from "@/lib/theme/useTheme";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAV_CONFIG, firstAccessiblePath, visibleWorkspaces, workspaceForPath } from "@/app/layout/nav-config";
import type { Workspace } from "@/app/layout/nav-config";
import { useAuthStore } from "@/lib/auth/store";
import { useEffectivePermissions } from "@/lib/auth/permissions";
import { useNavStore } from "@/lib/nav/store";
import { faqModuleForPath } from "@/lib/faqModules";
import { useFaqStore } from "@/lib/stores/faqStore";
import { cn } from "@/lib/utils/cn";
import type { SearchResult } from "@/lib/api/search";
import { globalSearch } from "@/lib/api/search";

const SEARCH_MAX_LEN = 128;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

// Small skeumorphic icon button — matches the back-arrow pattern used on detail pages
const iconBtn =
  "h-9 w-9 rounded-xl flex items-center justify-center transition-all " +
  "bg-gradient-to-b from-white to-slate-50/80 border border-black/[0.08] " +
  "shadow-[0_2px_4px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.9)] " +
  "hover:shadow-[0_3px_6px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.9)] " +
  "text-ink-500 hover:text-ink-700 " +
  "dark:bg-none dark:bg-white/[0.07] dark:border-white/[0.09] " +
  "dark:shadow-[0_2px_4px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.04)] " +
  "dark:hover:bg-white/[0.11] dark:hover:shadow-[0_3px_8px_rgba(0,0,0,0.4)] " +
  "dark:text-ink-400 dark:hover:text-ink-700 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1";

export function TopBar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const faqOpen = useFaqStore((st) => st.open);
  const toggleFaq = useFaqStore((st) => st.toggle);
  const faqHint = useFaqStore((st) => st.hint);
  const faqAuto = useFaqStore((st) => st.auto);
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false);
  const { byCode } = useEffectivePermissions();
  const setLastPath = useNavStore((s) => s.setLastPath);

  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const stored = localStorage.getItem("gymviz.theme");
    if (stored === "light" || stored === "dark") return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("gymviz.theme", theme);
  }, [theme]);

  const { data: publicTheme } = usePublicTheme();
  const tenantLogoUrl = theme === "dark"
    ? (publicTheme?.logo_dark_url || publicTheme?.logo_url || "")
    : (publicTheme?.logo_url || "");

  function toggleLocale() {
    const next = i18n.language === "ar" ? "en" : "ar";
    void i18n.changeLanguage(next);
  }

  function logout() {
    clear();
    navigate("/login", { replace: true });
  }

  const has = (key: string) => isSuperuser || key in byCode;
  const allVisible = visibleWorkspaces(NAV_CONFIG, has);
  const mainWorkspaces = allVisible.filter((ws) => ws.key !== "admin");
  const adminWorkspace = allVisible.find((ws) => ws.key === "admin");
  const activeWorkspace = workspaceForPath(pathname);

  function handleWorkspaceClick(ws: Workspace) {
    if (activeWorkspace && activeWorkspace.key !== ws.key) {
      setLastPath(activeWorkspace.key, pathname);
    }
    navigate(ws.key === "admin" ? ws.defaultPath : firstAccessiblePath(ws, has));
  }

  // ---------------------------------------------------------------------------
  // Global search
  // ---------------------------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const runSearch = useCallback(async (q: string) => {
    if (q.length < SEARCH_MIN_CHARS) {
      setSearchResults(null);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await globalSearch(q);
      const all = [...res.members, ...res.plans, ...res.invoices];
      setSearchResults(all);
      setSearchOpen(true);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value.slice(0, SEARCH_MAX_LEN);
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void runSearch(val), SEARCH_DEBOUNCE_MS);
  }

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") closeSearch();
  }

  function closeSearch() {
    setSearchOpen(false);
    setSearchFocused(false);
    setSearchQuery("");
    setSearchResults(null);
  }

  function handleResultClick(result: SearchResult) {
    closeSearch();
    if (result.url.startsWith("/") && !result.url.startsWith("//")) {
      navigate(result.url);
    }
  }

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setSearchOpen(false);
        setSearchFocused(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const displayName = user?.first_name || user?.username || "";
  const initials = (displayName.match(/\b\w/g) || []).slice(0, 2).join("").toUpperCase();

  return (
    <header className="border-b border-black/[0.06] dark:border-white/[0.06] bg-white/90 dark:bg-bg backdrop-blur-none">

      {/* ── Row 1: search + utility ── */}
      <div className="flex items-center gap-2 px-3 py-2 sm:gap-3 sm:px-4 max-md:pt-[max(0.5rem,env(safe-area-inset-top))]">

        {/* Logo mark — mobile only (sidebar is hidden on mobile) */}
        <div className="md:hidden shrink-0">
          <GymVizLogo variant="mark" size={28} ariaLabel={t("app.name")} />
        </div>

        {/* Search */}
        <div
          className="relative w-full min-w-0 max-w-xl"
          role="combobox"
          aria-expanded={searchOpen}
          aria-haspopup="listbox"
        >
          <Search
            className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="search"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => setSearchFocused(true)}
            placeholder={t("topbar.searchPlaceholder")}
            aria-label={t("topbar.searchAriaLabel")}
            aria-autocomplete="list"
            aria-controls={searchOpen ? "global-search-results" : undefined}
            maxLength={SEARCH_MAX_LEN}
            className={cn(
              "h-9 w-full rounded-xl border border-black/[0.08] dark:border-white/[0.08]",
              "bg-white/70 dark:bg-white/[0.08]",
              "shadow-[inset_0_1px_3px_rgba(0,0,0,0.05)] dark:shadow-[inset_0_1px_4px_rgba(0,0,0,0.2)]",
              "ps-9 pe-16 text-sm text-ink-900 dark:text-ink-900",
              // Phone: 16px text so iOS does not zoom the page when search gets focus.
              "max-md:h-10 max-md:pe-9 max-md:text-base",
              "placeholder:text-ink-400",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:border-primary-400/60",
              "transition-shadow",
            )}
          />
          {searchQuery ? (
            <button
              type="button"
              aria-label={t("topbar.searchClear")}
              onClick={closeSearch}
              className="absolute end-3 top-1/2 -translate-y-1/2 rounded-lg p-0.5 text-ink-400 hover:text-ink-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : (
            <kbd
              aria-hidden="true"
              className="hidden sm:inline-flex absolute end-3 top-1/2 -translate-y-1/2 items-center gap-0.5 rounded-lg border border-black/[0.08] dark:border-white/[0.08] bg-ink-50 dark:bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-ink-500 pointer-events-none"
            >
              ⌘K
            </kbd>
          )}

          {/* Results dropdown */}
          {(searchOpen || searchFocused) && (
            <div
              ref={dropdownRef}
              id="global-search-results"
              role="listbox"
              aria-label={t("topbar.searchResultsAriaLabel")}
              className="absolute top-full mt-2 w-full rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-surface shadow-[0_8px_24px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)] z-50 overflow-hidden"
            >
              {/* Quick actions — always shown at top */}
              <div className="px-3 pt-2.5 pb-1.5">
                <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-widest text-ink-400">
                  {t("topbar.quickActions")}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { closeSearch(); navigate("/members/new"); }}
                    className="flex items-center gap-1.5 rounded-lg border border-black/[0.08] dark:border-white/[0.08] bg-ink-50 dark:bg-white/[0.06] px-3 py-1.5 text-xs font-medium text-ink-700 dark:text-ink-200 hover:bg-primary-50 hover:border-primary-200/60 hover:text-primary-700 dark:hover:bg-primary-500/[0.15] dark:hover:text-primary-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("topbar.addMember")}
                  </button>
                  <button
                    type="button"
                    onClick={() => { closeSearch(); navigate("/memberships/sell"); }}
                    className="flex items-center gap-1.5 rounded-lg border border-black/[0.08] dark:border-white/[0.08] bg-ink-50 dark:bg-white/[0.06] px-3 py-1.5 text-xs font-medium text-ink-700 dark:text-ink-200 hover:bg-primary-50 hover:border-primary-200/60 hover:text-primary-700 dark:hover:bg-primary-500/[0.15] dark:hover:text-primary-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("topbar.sellPlan")}
                  </button>
                </div>
              </div>

              {searchQuery.length >= SEARCH_MIN_CHARS && (
                <div className="mx-3 my-1.5 border-t border-black/[0.06] dark:border-white/[0.06]" />
              )}

              {searchLoading && (
                <div className="px-4 py-3 text-sm text-ink-500">{t("topbar.searchLoading")}</div>
              )}
              {!searchLoading && searchResults !== null && searchResults.length === 0 && (
                <div className="px-4 py-3 text-sm text-ink-500">{t("topbar.searchNoResults")}</div>
              )}
              {!searchLoading && searchResults && searchResults.length > 0 && (
                <ul className="py-1.5">
                  {searchResults.map((r) => (
                    <li key={`${r.type}-${r.id}`}>
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => handleResultClick(r)}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-start hover:bg-ink-50 dark:hover:bg-white/[0.05] focus-visible:bg-ink-50 focus-visible:outline-none transition-colors"
                      >
                        <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 border border-primary-200/60 text-primary-700 dark:text-primary-300 text-[10px] font-bold uppercase">
                          {r.type.slice(0, 1)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-ink-900 dark:text-ink-900">{r.label}</span>
                          {r.sub && (
                            <span className="block truncate text-xs text-ink-500">{r.sub}</span>
                          )}
                        </span>
                        <span className="flex-shrink-0 text-[10px] font-bold text-ink-400 uppercase tracking-widest">
                          {t(`topbar.searchType.${r.type}`, r.type)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Utility buttons */}
        <div className="ms-auto flex shrink-0 items-center gap-2 sm:gap-3">

          {/* Tenant logo — hidden on mobile to keep topbar compact */}
          {tenantLogoUrl && (
            <div className="hidden sm:flex items-center px-3 py-1.5 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.04]">
              <img
                src={tenantLogoUrl}
                alt="Logo"
                style={{ height: 40, width: "auto", maxWidth: 160, objectFit: "contain" }}
                onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = "none"; }}
              />
            </div>
          )}

          {/* Locale toggle */}
          <button
            type="button"
            onClick={toggleLocale}
            className={cn(iconBtn, "w-auto px-3 text-xs font-bold tracking-wide max-md:hidden")}
            aria-label="Toggle language"
          >
            {i18n.language === "ar" ? "EN" : "ع"}
          </button>

          {/* Help & FAQ — opens the global FAQ popup on the current module */}
          <button
            type="button"
            onClick={() => toggleFaq(faqAuto ? faqModuleForPath(pathname) : null)}
            aria-pressed={faqOpen}
            className={cn(iconBtn, "relative min-h-[44px] w-auto gap-1.5 px-3 text-xs font-semibold max-md:hidden", faqOpen && "text-primary-600", faqHint && !faqOpen && "faq-bounce text-primary-600")}
            aria-label={t("button", { ns: "faq" })}
          >
            <HelpCircle className="h-4 w-4" />
            <span className="hidden lg:inline">{t("button", { ns: "faq" })}</span>
            {faqHint && !faqOpen && (
              <span className="absolute end-1 top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-accent-500" aria-hidden="true" />
            )}
          </button>

          {/* Notification center */}
          <NotificationCenter />

          {/* Dark mode toggle */}
          <button
            type="button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className={cn(iconBtn, "min-h-[44px] min-w-[44px] max-md:hidden")}
            aria-label={theme === "dark" ? t("page.themeLight") : t("page.themeDark")}
          >
            {theme === "dark"
              ? <Sun className="h-4 w-4" />
              : <Moon className="h-4 w-4" />}
          </button>

          {/* Profile dropdown */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t("topbar.profileAriaLabel", { name: displayName })}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl pe-3 ps-1.5 py-1 min-h-[44px] transition-all",
                    "bg-gradient-to-b from-white to-slate-50/80 border border-black/[0.08]",
                    "shadow-[0_2px_4px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.9)]",
                    "hover:shadow-[0_3px_6px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.9)]",
                    "dark:bg-none dark:bg-white/[0.07] dark:border-white/[0.09]",
                    "dark:shadow-[0_2px_4px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.04)]",
                    "dark:hover:bg-white/[0.11] dark:hover:shadow-[0_3px_8px_rgba(0,0,0,0.4)]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
                  )}
                >
                  <span
                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-b from-primary-50 to-primary-100/80 border border-primary-200/60 text-primary-700 font-bold text-xs shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)]"
                    aria-hidden="true"
                  >
                    {initials || <UserIcon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-sm font-semibold text-ink-900 dark:text-ink-900">{displayName}</span>
                    {user.branch_name && (
                      <span className="text-[10px] text-ink-500">{user.branch_name}</span>
                    )}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                <DropdownMenuLabel>{t("topbar.profileMenuLabel")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/settings/preferences")}>
                  <SlidersHorizontal className="h-4 w-4" />
                  {t("profilePreferences.topbarItem", "My preferences")}
                </DropdownMenuItem>
                {/* Phone: utilities removed from the top bar live here */}
                <div className="md:hidden">
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTheme(theme === "dark" ? "light" : "dark"); }}>
                    {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    {theme === "dark" ? t("page.themeLight") : t("page.themeDark")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={toggleLocale}>
                    <Languages className="h-4 w-4" />
                    {i18n.language === "ar" ? "English" : "العربية"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => toggleFaq(faqAuto ? faqModuleForPath(pathname) : null)}>
                    <HelpCircle className="h-4 w-4" />
                    {t("button", { ns: "faq" })}
                  </DropdownMenuItem>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={logout}>
                  <LogOut className="h-4 w-4" />
                  {t("auth.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* ── Row 2: workspace switcher (desktop only) ── */}
      <div className="hidden md:flex items-center gap-2 px-4 pb-2 overflow-x-auto">
        <nav
          aria-label={t("topbar.workspacesAriaLabel")}
          className={cn(
            "flex items-center gap-0.5 flex-1 min-w-0 p-1 rounded-xl overflow-x-auto",
            "bg-ink-200/40 dark:bg-white/[0.06]",
            "backdrop-blur-sm",
            "shadow-[inset_0_1px_3px_rgba(0,0,0,0.10),inset_0_1px_1px_rgba(0,0,0,0.06)]",
            "dark:shadow-[inset_0_2px_4px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.04)]",
            "border border-black/[0.06] dark:border-white/[0.10]",
          )}
        >
          {mainWorkspaces.map((ws) => {
            const Icon = ws.icon;
            const isActive = activeWorkspace?.key === ws.key;
            return (
              <button
                key={ws.key}
                type="button"
                aria-current={isActive ? "page" : undefined}
                onClick={() => handleWorkspaceClick(ws)}
                className={cn(
                  "whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-all duration-200",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
                  isActive
                    ? [
                        "bg-gradient-to-b from-white to-slate-50",
                        "shadow-[0_2px_5px_rgba(0,0,0,0.13),0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,1)]",
                        "border border-black/[0.08]",
                        "text-primary-700 font-semibold",
                        "-translate-y-0.5",
                        "dark:bg-none dark:bg-primary-500/[0.18]",
                        "dark:shadow-[0_2px_8px_rgb(var(--color-primary-500)/0.35),inset_0_1px_0_rgba(255,255,255,0.08)]",
                        "dark:border-primary-500/25",
                        "dark:text-primary-300",
                      ]
                    : [
                        "border border-transparent",
                        "text-ink-500 dark:text-ink-200",
                        "hover:-translate-y-0.5",
                        "hover:bg-gradient-to-b hover:from-white/80 hover:to-white/60",
                        "dark:hover:bg-white/[0.08]",
                        "hover:shadow-[0_2px_6px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.8)]",
                        "dark:hover:shadow-[0_2px_6px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.10)]",
                        "hover:border-black/[0.06] dark:hover:border-white/[0.10]",
                        "hover:text-ink-800 dark:hover:text-ink-100",
                      ],
                )}
              >
                <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                {t(ws.labelKey)}
              </button>
            );
          })}
        </nav>

        {/* Admin — separate raised pill, pinned end */}
        {adminWorkspace && (() => {
          const isActive = activeWorkspace?.key === "admin";
          return (
            <button
              type="button"
              aria-current={isActive ? "page" : undefined}
              onClick={() => handleWorkspaceClick(adminWorkspace)}
              className={cn(
                "flex-shrink-0 flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm transition-all duration-200",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
                "border",
                isActive
                  ? [
                      "bg-gradient-to-b from-white to-slate-50",
                      "shadow-[0_2px_5px_rgba(0,0,0,0.13),0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,1)]",
                      "border-black/[0.08]",
                      "text-primary-700 font-semibold",
                      "dark:bg-none dark:bg-primary-500/[0.18]",
                      "dark:shadow-[0_2px_8px_rgb(var(--color-primary-500)/0.35),inset_0_1px_0_rgba(255,255,255,0.08)]",
                      "dark:border-primary-500/25",
                      "dark:text-primary-300",
                    ]
                  : [
                      "bg-ink-200/40 dark:bg-white/[0.06]",
                      "border-black/[0.06] dark:border-white/[0.10]",
                      "shadow-[inset_0_1px_3px_rgba(0,0,0,0.10)] dark:shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]",
                      "text-ink-500 dark:text-ink-200",
                      "hover:-translate-y-0.5",
                      "hover:bg-gradient-to-b hover:from-white/80 hover:to-white/60 dark:hover:bg-white/[0.10]",
                      "hover:shadow-[0_2px_6px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,0.8)] dark:hover:shadow-[0_2px_6px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.10)]",
                      "hover:text-ink-800 dark:hover:text-ink-100",
                    ],
              )}
            >
              <Settings className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{t(adminWorkspace.labelKey)}</span>
            </button>
          );
        })()}
      </div>
    </header>
  );
}
